import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HeartPulse } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState(sessionStorage.getItem("dtd_admin_key") || "");
  const [input, setInput] = useState("");
  const [enquiries, setEnquiries] = useState(null);

  const load = useCallback(async (k) => {
    try {
      const res = await axios.get(`${API}/enquiries`, { headers: { "X-Admin-Key": k } });
      setEnquiries(res.data.enquiries);
    } catch {
      sessionStorage.removeItem("dtd_admin_key");
      setKey("");
      setEnquiries(null);
      toast.error("Incorrect admin key");
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, [key, load]);

  const login = (e) => {
    e.preventDefault();
    sessionStorage.setItem("dtd_admin_key", input);
    setKey(input);
  };

  if (!key || enquiries === null) {
    return (
      <div className="min-h-screen bg-[#FAF6EE] text-[#1B4332] flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm border border-[#E3DCC9] bg-[#F1EBDD] p-10">
          <HeartPulse className="w-8 h-8 text-[#EA580C] mb-6" />
          <h1 className="font-display font-semibold uppercase tracking-tight text-2xl">Admin access</h1>
          <p className="mt-2 text-sm text-[#66765F]">Enter your admin key to view enquiries.</p>
          <input
            type="password"
            data-testid="admin-key-input"
            className="field-input mt-8"
            placeholder="Admin key"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <button type="submit" data-testid="admin-login-btn" className="mt-8 w-full bg-[#EA580C] text-[#FAF6EE] font-semibold text-sm tracking-[0.1em] uppercase py-4 hover:bg-[#1B4332] transition-colors duration-300">
            Sign in
          </button>
          <Link to="/" className="mt-6 block text-xs text-[#66765F] hover:text-[#1B4332] transition-colors duration-300">← Back to site</Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF6EE] text-[#1B4332]" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display font-semibold uppercase tracking-tight text-3xl">Enquiries</h1>
            <p className="mt-1 text-sm text-[#66765F]">{enquiries.length} received</p>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={() => { sessionStorage.removeItem("dtd_admin_key"); setKey(""); setEnquiries(null); }}
            className="border border-[#E3DCC9] px-6 py-3 text-xs tracking-[0.15em] uppercase hover:border-[#EA580C] transition-colors duration-300"
          >
            Log out
          </button>
        </div>
        <div className="mt-10 space-y-px bg-[#E3DCC9] border border-[#E3DCC9]">
          {enquiries.length === 0 && (
            <p className="bg-[#FAF6EE] p-10 text-sm text-[#66765F]" data-testid="admin-empty">No enquiries yet.</p>
          )}
          {enquiries.map((e) => (
            <div key={e.id} className="bg-[#FAF6EE] p-6 grid md:grid-cols-4 gap-4" data-testid={`admin-enquiry-${e.id}`}>
              <div>
                <p className="font-semibold">{e.name}</p>
                <p className="text-sm text-[#66765F]">{e.company || "—"}</p>
              </div>
              <div className="text-sm">
                <p>{e.email}</p>
                <p className="text-[#66765F]">{e.phone || "—"}</p>
              </div>
              <div className="text-sm">
                <p className="text-[#EA580C]">{e.package || "Not specified"}</p>
                <p className="text-[#66765F] mt-1">{e.message || ""}</p>
              </div>
              <p className="text-xs text-[#66765F] md:text-right">{new Date(e.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
